import { ActionType, createReducer } from "typesafe-actions";
import * as actions from "store/features/materialSpecs/specs/specs.actions";
import { resetApiError } from "store/features/general";
import {
    AdDetailsViewModel,
    createFilteredMaterialSpecs,
    MaterialSpecificationViewModel,
} from "api/factories/creatives.factory";
import {
    fetchBookings,
    uploadMaterials,
    fetchThirdPartyTag,
    submitThirdPartyTag,
    fetchValidationReport,
    removeMaterial,
    fetchPrintValidationReport,
    submitNewCreativeThirdPartyTag,
    updateMaterialSpecStatus,
    setMaterialSpecWorkingStatus,
    setNewMaterialVersion,
    submitPrintMaterialDescription,
    submitPrintMaterialAdServerProperties,
    updateMaterialStatus,
    setSelectedPage,
    updateExistingSpecProperties,
} from "store/features/materialSpecs/specs/specs.actions";
import { AppConfig } from "api";
import {
    updateExistingSpecStatusSuccess,
    updateMaterialSpecStatusRequest,
} from "store/features/materialSpecs/specs/handlers/updateExistingSpecStatus.handler";

export type CreativesAction = ActionType<typeof actions>;

export type SpecsState = Readonly<{
    errorMessage?: string;
    hasError: boolean;
    isProcessing: boolean;
    isThirdPartyProcessing: boolean; // TODO: this should be in third party sub-reducer
    isPreflightReportLoading: boolean; // TODO: this should be in preflight sub-reducer
    materialSpecificationId?: number;
    materialSpecifications: MaterialSpecificationViewModel[];
    adDetails: AdDetailsViewModel | null;
    config: AppConfig;
    user: string;
}>;

export const creativesState: SpecsState = {
    hasError: false,
    isThirdPartyProcessing: false,
    isPreflightReportLoading: false,
    isProcessing: false,
    config: {
        lineItemHeader: {
            rows: [],
            header: [],
        },
        mimeTypes: [],
        showSgCode: false,
    },
    materialSpecifications: [],
    adDetails: null,
    user: "",
};

export const specsReducer = createReducer<SpecsState, CreativesAction>(creativesState)
    .handleAction([fetchBookings.request], (state) => {
        return {
            ...state,
            isProcessing: true,
        };
    })
    .handleAction([fetchThirdPartyTag.request], (state) => {
        return {
            ...state,
            isThirdPartyProcessing: true,
        };
    })
    .handleAction(
        submitPrintMaterialAdServerProperties.request,
        (state, { payload: { adMaterialId, materialSpecificationId } }) => {
            return {
                ...state,
                isThirdPartyProcessing: true,
                materialSpecifications: state.materialSpecifications.map((materialSpec) => {
                    return {
                        ...materialSpec,
                        materials: materialSpec.materials.map((material) => {
                            return {
                                ...material,
                                isUploading:
                                    material.id === adMaterialId && materialSpec.id === materialSpecificationId,
                            };
                        }),
                    };
                }),
            };
        }
    )
    .handleAction(
        submitThirdPartyTag.request,
        (
            state,
            { payload: { adMaterialId, materialSpecificationId, sgCode, tag, pixelTracker, description, url } }
        ) => {
            return {
                ...state,
                isThirdPartyProcessing: true,
                materialSpecifications: state.materialSpecifications.map((materialSpec) => {
                    return {
                        ...materialSpec,
                        materials: materialSpec.materials.map((material) => {
                            if (material.id === adMaterialId) {
                                return {
                                    ...material,
                                    sgCode: sgCode || material.sgCode,
                                    tag: tag || material.tag,
                                    pixelTracker: pixelTracker || material.pixelTracker,
                                    description: description || material.description,
                                    url: url || material.url,
                                    isUploading:
                                        material.id === adMaterialId && materialSpec.id === materialSpecificationId,
                                };
                            }

                            return material;
                        }),
                    };
                }),
            };
        }
    )
    .handleAction([uploadMaterials.request], (state, { payload: { materialId, materialSpecId } }) => {
        return {
            ...state,
            isPreflightReportLoading: true,
            materialSpecifications: state.materialSpecifications.map((materialSpec) => {
                return {
                    ...materialSpec,
                    isUploadingOptional: !materialId && materialSpecId === materialSpec.id,
                    materials: materialSpec.materials.map((material) => {
                        const isMaterialProcessing = material.id === materialId && materialSpec.id === materialSpecId;

                        return {
                            ...material,
                            preflightReport: null, // TODO: temporarily clear reports for all materials
                            printPreflightReport: null, // TODO: temporarily clear reports for all materials
                            // leave materials that are already processing
                            isUploading: material.isUploading ? material.isUploading : isMaterialProcessing,
                        };
                    }),
                };
            }),
        };
    })
    .handleAction(updateMaterialStatus, (state, { payload: { materialId, status } }) => {
        return {
            ...state,
            materialSpecifications: state.materialSpecifications.map((materialSpec) => {
                return {
                    ...materialSpec,
                    materials: materialSpec.materials.map((material) => {
                        if (material.id === materialId) {
                            return {
                                ...material,
                                status,
                            };
                        }

                        return material;
                    }),
                };
            }),
        };
    })
    .handleAction(setSelectedPage, (state, { payload: { materialId, direction } }) => {
        return {
            ...state,
            materialSpecifications: state.materialSpecifications.map((materialSpec) => {
                return {
                    ...materialSpec,
                    materials: materialSpec.materials.map((material) => {
                        if (material.id === materialId && material.materialPagesPreview) {
                            const { selectedPage } = material.materialPagesPreview;
                            const newPage = direction === "PREVIOUS" ? selectedPage - 1 : selectedPage + 1;

                            if (newPage >= 0 && newPage < material.materialPagesPreview.pages.length) {
                                return {
                                    ...material,
                                    materialPagesPreview: {
                                        ...material.materialPagesPreview,
                                        selectedPage: newPage,
                                    },
                                };
                            }
                        }

                        return material;
                    }),
                };
            }),
        };
    })
    .handleAction([removeMaterial.request], (state, { payload: { materialId, materialSpecId } }) => {
        return {
            ...state,
            materialSpecifications: state.materialSpecifications.map((materialSpec) => {
                return {
                    ...materialSpec,
                    materials: materialSpec.materials.map((material) => {
                        return {
                            ...material,
                            isDeleting: material.id === materialId && materialSpec.id === materialSpecId,
                        };
                    }),
                };
            }),
        };
    })
    .handleAction([submitNewCreativeThirdPartyTag.request], (state, { payload: { materialSpecificationId } }) => {
        return {
            ...state,
            isProcessing: true,
            materialSpecifications: state.materialSpecifications.map((materialSpec) => {
                return {
                    ...materialSpec,
                    isUploadingOptional: materialSpecificationId === materialSpec.id,
                    materials: [...materialSpec.materials],
                };
            }),
        };
    })
    .handleAction(fetchBookings.success, (state, { payload: { materialSpecifications, adDetails, config, user } }) => {
        return {
            ...state,
            isProcessing: false,
            config,
            materialSpecifications,
            adDetails,
            user,
        };
    })
    .handleAction(uploadMaterials.success, (state, { payload: { currentMaterialSpecification, materials } }) => {
        return {
            ...state,
            materialSpecifications: createFilteredMaterialSpecs(
                currentMaterialSpecification,
                state.materialSpecifications,
                materials
            ),
        };
    })
    .handleAction(
        fetchThirdPartyTag.success,
        (state, { payload: { materialSpecId, adMaterialId, tag, sgCode, description } }) => {
            return {
                ...state,
                isThirdPartyProcessing: false,
                materialSpecifications: state.materialSpecifications.map((materialSpecification) => {
                    if (materialSpecId === materialSpecification.id) {
                        return {
                            ...materialSpecification,
                            materials: [
                                ...materialSpecification.materials.map((adMaterial) => {
                                    if (adMaterial.id === adMaterialId) {
                                        return {
                                            ...adMaterial,
                                            sgCode,
                                            tag,
                                            description,
                                        };
                                    }

                                    return adMaterial;
                                }),
                            ],
                        };
                    }

                    return materialSpecification;
                }),
            };
        }
    )
    .handleAction(
        submitThirdPartyTag.success as never,
        (state, { payload: { materialSpecificationId, adMaterialId, tag, pixelTracker, description, url } }) => {
            return {
                ...state,
                isThirdPartyProcessing: false,
                materialSpecifications: state.materialSpecifications.map((materialSpecification) => {
                    if (materialSpecificationId === materialSpecification.id) {
                        return {
                            ...materialSpecification,
                            materials: [
                                ...materialSpecification.materials.map((adMaterial) => {
                                    if (adMaterial.id === adMaterialId) {
                                        return {
                                            ...adMaterial,
                                            pixelTracker: pixelTracker || adMaterial.pixelTracker,
                                            description: description || adMaterial.description,
                                            url: url || adMaterial.url,
                                            tag: tag || adMaterial.tag,
                                            isUploading: false,
                                        };
                                    }

                                    return adMaterial;
                                }),
                            ],
                        };
                    }

                    return materialSpecification;
                }),
            };
        }
    )
    .handleAction(
        submitPrintMaterialAdServerProperties.success as never,
        (state, { payload: { materialSpecificationId, adMaterialId, url, description } }) => {
            return {
                ...state,
                isThirdPartyProcessing: false,
                materialSpecifications: state.materialSpecifications.map((materialSpecification) => {
                    if (materialSpecificationId === materialSpecification.id) {
                        return {
                            ...materialSpecification,
                            customerNote: description,
                            materials: [
                                ...materialSpecification.materials.map((adMaterial) => {
                                    if (adMaterial.id === adMaterialId) {
                                        return {
                                            ...adMaterial,
                                            url,
                                            isUploading: false,
                                        };
                                    }

                                    return adMaterial;
                                }),
                            ],
                        };
                    }

                    return materialSpecification;
                }),
            };
        }
    )
    .handleAction(
        submitNewCreativeThirdPartyTag.success as never,
        (state, { payload: { currentMaterialSpecification, materials } }) => {
            const materialSpecifications = createFilteredMaterialSpecs(
                currentMaterialSpecification,
                state.materialSpecifications,
                materials
            );

            return {
                ...state,
                isProcessing: false,
                materialSpecifications,
            };
        }
    )
    .handleAction(
        submitPrintMaterialDescription.success as never,
        (state, { payload: { materialSpecificationId, description } }) => {
            return {
                ...state,
                isProcessing: false,
                materialSpecifications: state.materialSpecifications.map((materialSpecification) => {
                    if (materialSpecificationId === materialSpecification.id) {
                        return {
                            ...materialSpecification,
                            customerNote: description,
                        };
                    }

                    return materialSpecification;
                }),
            };
        }
    )
    // TODO: for now there is no need to do anything on request(?)
    .handleAction([fetchValidationReport.request, fetchPrintValidationReport.request], (state) => {
        return {
            ...state,
            isPreflightReportLoading: true,
        };
    })
    .handleAction(fetchValidationReport.success, (state, { payload }) => {
        return {
            ...state,
            isPreflightReportLoading: false,
            materialSpecifications: state.materialSpecifications.map((materialSpecification) => {
                return {
                    ...materialSpecification,
                    materials: materialSpecification.materials.map(({ id, preflightReport, ...material }) => {
                        return {
                            ...material,
                            id,
                            preflightReport: payload.materialId === id ? payload.preflightReport : preflightReport,
                        };
                    }),
                };
            }),
        };
    })
    .handleAction(fetchPrintValidationReport.success, (state, { payload }) => {
        return {
            ...state,
            isPreflightReportLoading: false,
            materialSpecifications: state.materialSpecifications.map((materialSpecification) => {
                return {
                    ...materialSpecification,
                    materials: materialSpecification.materials.map(({ id, printPreflightReport, ...material }) => {
                        return {
                            ...material,
                            id,
                            printPreflightReport:
                                payload.materialId === id ? payload.preflightReport : printPreflightReport,
                        };
                    }),
                };
            }),
        };
    })
    .handleAction(removeMaterial.success, (state, { payload: { materialSpecId, deletedMaterialId, materials } }) => {
        // replace with new state
        if (materials.length) {
            return {
                ...state,
                materialSpecifications: createFilteredMaterialSpecs(
                    materialSpecId,
                    state.materialSpecifications,
                    materials
                ),
            };
        }

        // remove completely material from state
        return {
            ...state,
            materialSpecifications: state.materialSpecifications.map((materialSpecification) => {
                return {
                    ...materialSpecification,
                    materials: materialSpecification.materials.filter((material) => material.id !== deletedMaterialId),
                };
            }),
        };
    })
    .handleAction(
        [updateMaterialSpecStatus.request, updateExistingSpecProperties.request],
        updateMaterialSpecStatusRequest
    )
    .handleAction(updateExistingSpecProperties.success, updateExistingSpecStatusSuccess)
    .handleAction([updateMaterialSpecStatus.success, setMaterialSpecWorkingStatus], (state, { payload }) => {
        return {
            ...state,
            materialSpecifications: state.materialSpecifications.map((materialSpec) => {
                if (payload.materialSpec.id === materialSpec.id) {
                    return {
                        ...materialSpec,
                        // TODO: temporary flag
                        isWorking: payload.materialSpec.status.code === 3,
                        isStatusUpdateProcessing: payload.materialSpec.status.code === 3,
                        status: payload.materialSpec.status,
                        materials: materialSpec.materials.map((material) => {
                            const [newMaterial] = payload.materialSpec.materials.filter(({ id }) => id === material.id);

                            if (newMaterial) {
                                return {
                                    ...material,
                                    status: newMaterial.status,
                                };
                            }

                            return material;
                        }),
                    };
                }

                return materialSpec;
            }),
        };
    })
    .handleAction(setNewMaterialVersion, (state, { payload: { material, materialSpecId, selectedMaterialId } }) => {
        return {
            ...state,
            materialSpecifications: state.materialSpecifications.map((materialSpec) => {
                if (materialSpecId === materialSpec.id) {
                    return {
                        ...materialSpec,
                        materials: materialSpec.materials.map((currentMaterial) => {
                            // replace material with new (historical)
                            if (selectedMaterialId === currentMaterial.id) {
                                return material;
                            }

                            return currentMaterial;
                        }),
                    };
                }

                return materialSpec;
            }),
        };
    })
    .handleAction(
        submitThirdPartyTag.failure as never,
        (state, { payload: { materialSpecificationId, adMaterialId, message } }) => {
            return {
                ...state,
                isThirdPartyProcessing: false,
                isPreflightReportLoading: false,
                isProcessing: false,
                hasError: true,
                errorMessage: message,
                materialSpecifications: state.materialSpecifications.map((materialSpecification) => {
                    if (materialSpecificationId === materialSpecification.id) {
                        // if uploading existing material failed
                        if (adMaterialId) {
                            return {
                                ...materialSpecification,
                                materials: [
                                    ...materialSpecification.materials.map((adMaterial) => {
                                        if (adMaterial.id === adMaterialId) {
                                            return {
                                                ...adMaterial,
                                                isUploading: false,
                                            };
                                        }

                                        return adMaterial;
                                    }),
                                ],
                            };
                        }
                    }

                    // if uploading new (optional) material failed
                    return {
                        ...materialSpecification,
                        isUploadingOptional: false,
                    };
                }),
            };
        }
    )
    .handleAction(
        [
            fetchBookings.failure,
            uploadMaterials.failure,
            fetchThirdPartyTag.failure,
            submitNewCreativeThirdPartyTag.failure,
            submitPrintMaterialDescription.failure,
            submitPrintMaterialAdServerProperties.failure,
            fetchValidationReport.failure,
            fetchPrintValidationReport.failure,
            removeMaterial.failure,
            updateMaterialSpecStatus.failure,
        ],
        (state, action) => {
            return {
                ...state,
                isThirdPartyProcessing: false,
                isPreflightReportLoading: false,
                isProcessing: false,
                hasError: true,
                errorMessage: action.payload.message,
            };
        }
    )
    .handleAction(resetApiError as never, (state) => {
        return {
            ...state,
            hasError: false,
            errorMessage: undefined,
        };
    });
